import React from 'react';
import PropTypes from "prop-types";
import classNames from "classnames";

import { Icon } from "rhombus-react";

/* ensure entered value is within the bounds set on the input
 * solves for an issue with <input/> where using the arrow works with min/max, but typing disregards min/max
 */
function normalizeNumbers(value, min, max, type = "int"){
	let val = (type === "int") ? parseInt(value) : parseFloat(value);

	if(!isNaN(min) && val < min){
		return min;
	}
	if(!isNaN(max) && val > max){
		return max;
	}
	if(!isNaN(val)){
		return val.toString();
	}
	return value;
}

const PROPTYPE_THEME = PropTypes.oneOf([
	'default',
	'primary',
	'white',
	'blue',
	'purple',
	'pink',
	'orange',
	'green',
	'red',
	'grey',
	'yellow',
	'teal',
]);

export default class Input extends React.Component {

	static displayName = 'Input';


	static contextTypes = {
		rhombusTheme: PROPTYPE_THEME,
	};

	static childContextTypes = {
		iconClassPrefix: PropTypes.string,
	};

	static defaultProps = {
		name:"",
		placeholder:"",
		icon:null,
		iconPos: 'left',
		type: 'text',
		normalizeType: 'none',
		pre: null,
		post: null,
	};

	static propTypes = {
		UID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

		/** Sets the theme/colour for the input */
		theme: PROPTYPE_THEME,

		/** The icon to be displayed, if any */
		icon: PropTypes.string,

		/** Position of the icon, either 'left' or 'right', immutable once set */
		iconPos: PropTypes.oneOf(["left", "right"]),

		/** Sets the placeholder text */
		placeholder: PropTypes.string,

		/** Sets the name/url param for this field */
		name: PropTypes.string.isRequired,

		/** Sets the input type */
		type: PropTypes.oneOf([
			'text', 'password', 'datetime', 'datetime-local', 'date', 'month', 'time', 'week',
			'number', 'email', 'url', 'search', 'tel', 'color', 'range', 'textarea'
		]),

		/** should number inputs be normalized */
		normalizeType: PropTypes.oneOf(['none', 'int', 'float']),

		/** Content to display inline with the `input` element, inside the border
		 * similar to the `icon` prop, but more flexible.
		 * `pre` is added before the input element.
		 * `post` is added after the input element.
		*/
		pre: PropTypes.node,
		post: PropTypes.node,
	};




	getChildContext() {
		return {
			iconClassPrefix: `e-form__control--icon e-form__control--icon-${this.props.iconPos}`,
		};
	}

	constructor(props, context){
		super(props, context);
		this.state = { value: props.value || props.defaultValue || "" };

		this.onChange = this.onChange.bind(this);
	}
	componentWillReceiveProps(nextProps){
		if(nextProps.value !== this.props.value) {
			this.setState({ value: nextProps.value || "" });
		}
	}

	onChange(e){
		let { value } = e.target
			, { normalizeType, type, min, max, onChange } = this.props
			;

		if(type === "number" && normalizeType !== "none"){
			value = normalizeNumbers(value, min, max, normalizeType);
		}

		this.setState({ value }, () => {
			if(onChange) {
				onChange(value);
			}
		});
	}

	render() {
		/* eslint-disable no-unused-vars */
		/*
			There are some unused vars in this block, but it pulls them out of remainderProps, which keeps them
			from being passed into the input element and throwing warnings.
		*/
		let {
				icon,
				iconPos,
				theme,
				UID,
				className,
				showCharCount,
				charMax,
				defaultValue,
				type,
				hasError,
				normalizeType,
				pre,
				post,
				...remainderProps
		  } = this.props
		  , wrapperClass = {
				"e-form__control":true,
				"with-icon":!!(icon || pre || post),
				"is-text-area":(type === "textarea"),
				'e-form--error':hasError,
		  }
		  , extraProps = {}
		  , $node = null
		  ;
		/* eslint-enable */

		if(!theme) {
			theme = this.context.rhombusTheme;
		}

		if(type === "number"){
			// add step="any" to number fields if step is not specified to fix an issue with firefox incorrectly highlighting a field as if it is in error
			if(typeof(remainderProps.step) === "undefined"){
				remainderProps.step = "any";
			}

			if(normalizeType !== "none"){
				extraProps.normalize = (value) => { normalizeNumbers(value, remainderProps.min, remainderProps.max, normalizeType); };
			}
		}


		if(type === "textarea"){
			$node = (
				<textarea
					{...extraProps}
					{...remainderProps}
					value     = {this.state.value}
					onChange  = {this.onChange}
					id        = {UID}
					className = {`e-form__input text-area ${className}`}
				/>
			);
		} else {
			$node = (
				<input
					{...extraProps}
					{...remainderProps}
					type      = {type}
					value     = {this.state.value}
					onChange  = {this.onChange}
					id        = {UID}
					className = {`e-form__input ${className}`}
				/>
			);
		}

		return (
			<div className={classNames(wrapperClass)}>
				{ icon ? <Icon name={icon} /> : null }
				{ pre ? <div className="inline-content pre-content">{pre}</div> : null }
				{$node}
				{ post ? <div className="inline-content post-content">{post}</div> : null }
			</div>
		);
	}

}
